/**
 * Experiment Circuit Breaker
 * 
 * Centralized service for evaluating experiment decisions with three-layer protection.
 * Used by both SSR and client-side code to ensure consistent experiment evaluation.
 * 
 * ## Architecture
 * 
 * The circuit breaker implements a defense-in-depth pattern with three validation layers:
 * 
 * 1. **Tenant Config Gate** - Code-level whitelist check
 *    - Ensures experiments are explicitly enabled in tenant configuration
 *    - First line of defense against misconfiguration
 * 
 * 2. **Payload Condition Gate** - Dashboard configuration validation
 *    - Checks GrowthBook payload for explicit tenant exclusions
 *    - Catches dashboard-level tenant targeting misconfigurations
 * 
 * 3. **GrowthBook Evaluation** - Native targeting and rollout
 *    - Applies GrowthBook's targeting rules and rollout percentages
 *    - Final layer where user bucketing occurs
 * 
 * ## Overrides
 * 
 * - **ModHeader** (`x-gb-force`) - Works in all environments, bypasses all layers
 * - **URL params** (`?gb_force_`) - Only works in dev/preview, bypasses all layers
 * 
 * ## Usage
 * 
 * ```typescript
 * // SSR evaluation
 * const layer1 = checkTenantConfig(key, tenantId, ExperimentContext.SSR);
 * if (layer1) return layer1.allowed;
 * 
 * const layer2 = checkPayloadCondition(key, payload, tenantId, ExperimentContext.SSR);
 * if (layer2) return layer2.allowed;
 * 
 * const result = evaluateGrowthBookResult(key, gbResult, tenantId, ExperimentContext.SSR);
 * return result.allowed;
 * ```
 * 
 * @see docs/guides/experiments.md - Full implementation guide
 */

export * from './types';
export * from './tenant-config-gate';
export * from './payload-condition-gate';
export * from './growthbook-evaluation';
